import { defineStore } from "pinia";
import { computed, ref } from "vue";
import AccessibilityFontFamilyEnum from "@shared-enums/accessibility/accessiblity.font.family.enum";
import AccessibilityFontSizeEnum from "@shared-enums/accessibility/accessibility.font.size.enum";
import AccessibilityThemeEnum from "@shared-enums/accessibility/accessibility.theme.enum";
import AccessibilityAppColorEnum from "@shared-enums/accessibility/app.color.enum";
import AccessibilityColorEnum from "@shared-enums/accessibility/accessiblity.color.enum";
import AccessibilityCursorEnum from "@shared-enums/accessibility/accessibility.cursor.enum";
import AccessibilityCursorSizeEnum from "@shared-enums/accessibility/accessibility.cursor.size.enum";
import AccessibilityCursorColorEnum from "@shared-enums/accessibility/accessibility.cursor.color.enum";
import AccessibilityLetterSpacingEnum from "@shared-enums/accessibility/accessiblity.letter.spacing.enum";
import AccessibilityTtsRateEnum from "@shared-enums/accessibility/accessibility.tts.rate.enum";
import AccessibilityTtsVoiceEnum from "@shared-enums/accessibility/accessibility.tts.voice.enum";
import AccessibilitySyllablesColorsEnum from "@shared-enums/accessibility/accessibility.syllables.colors.enum";
import AccessibilityReadingMaskHeightEnum from "@shared-enums/accessibility/accessibility.reading-mask.height.enum";
import AccessibilityReadingMaskOpacityEnum from "@shared-enums/accessibility/accessibility.reading-mask.opacity.enum";
import { stopAllSpeaking } from "@shared-utils/accessibility/tts.utils";

const useAccessibilityStore = defineStore(
	"accessibility",
	() =>
	{
		const isScrolling = ref<boolean>(false);
		const animations = ref<boolean>(true);
		const haptics = ref<boolean>(true);
		const crashlytics = ref<boolean>(true);
		const hoverOnFocus = ref<boolean>(false);
		const tts = ref<boolean>(false);
		const ttsRate = ref<keyof typeof AccessibilityTtsRateEnum>("accessibility-tts-rate-normal");
		const ttsVoice = ref<keyof typeof AccessibilityTtsVoiceEnum>("accessibility-tts-voice-feminine");
		let ttsObject = window?.speechSynthesis;

		const syllables = ref<boolean>(false);
		const syllablesColors = ref<keyof typeof AccessibilitySyllablesColorsEnum>("accessibility-syllables-blue-red");

		const readingMask = ref<boolean>(false);
		const readingMaskHeight = ref<keyof typeof AccessibilityReadingMaskHeightEnum>("accessibility-reading-mask-height-2");
		const readingMaskOpacity = ref<keyof typeof AccessibilityReadingMaskOpacityEnum>("accessibility-reading-mask-opacity-medium");

		const theme = ref<keyof typeof AccessibilityThemeEnum>("accessibility-theme-light");
		const color = ref<keyof typeof AccessibilityColorEnum>("accessibility-color-default");
		const blueLight = ref<number>(0);
		const fontFamily = ref<keyof typeof AccessibilityFontFamilyEnum>("accessibility-font-family-default");
		const fontSize = ref<keyof typeof AccessibilityFontSizeEnum>("accessibility-font-size-default");
		const letterSpacing = ref<keyof typeof AccessibilityLetterSpacingEnum>("accessibility-letter-spacing-default");
		const cursor = ref<keyof typeof AccessibilityCursorEnum>("accessibility-cursor-default");
		const cursorSize = ref<keyof typeof AccessibilityCursorSizeEnum>("accessibility-cursor-size-x1");
		const cursorColor = ref<keyof typeof AccessibilityCursorColorEnum>("accessibility-cursor-color-default");

		const appColor = ref<string | null>(null);

		const modeNight = ref<boolean>(false);
		const modeDyslexia = ref<boolean>(false);
		const modeDyslexia2 = ref<boolean>(false);
		const modeMotorImpairments = ref<boolean>(false);
		const modeLowVision = ref<boolean>(false);
		const modeBlurredVision = ref<boolean>(false);
		const modeDeaf = ref<boolean>(false);
		const modeADHD = ref<boolean>(false);
		const modeMigraine = ref<boolean>(false);
		const modeSeniors = ref<boolean>(false);
		const modeEyeStrain = ref<boolean>(false);
		const modeLongReading = ref<boolean>(false);

		const getTheme = computed(() => theme.value);
		const isDarkTheme = computed(() => theme.value == "accessibility-theme-dark");
		const isFs200 = computed(() => fontSize.value === "accessibility-font-size-200");
		const needsCompactLayout = computed(() =>
			fontSize.value === "accessibility-font-size-200"
			&& (letterSpacing.value === "accessibility-letter-spacing-wide"
				|| letterSpacing.value === "accessibility-letter-spacing-wider")
		);
		const needsExtraCompactLayout = computed(() =>
			needsCompactLayout.value
			&& fontFamily.value === "accessibility-font-family-open-dyslexic"
		);

		const applyAppColor = (value: string | null): void =>
		{
			const html = getHtmlTag();
			if (!html) return;

			Array.from(html.classList)
				.filter(c => c.startsWith("app-color-"))
				.forEach(c => html.classList.remove(c));

			// "app-color-vert" est le défaut → on stocke null (pas de classe)
			const isDefault = value === "app-color-vert";
			if (value && !isDefault)
			{
				html.classList.add(value);
			}

			appColor.value = isDefault ? null : value;
		};

		const initAppColor = (): void =>
		{
			applyAppColor(appColor.value);
		};

		const toggleAppColor = (): void =>
		{
			// Cycle dans l'ordre déclaré de l'enum
			const cycle = Object.keys(AccessibilityAppColorEnum);
			const current = appColor.value || "app-color-vert";
			const currentIndex = cycle.indexOf(current);
			const nextIndex = (currentIndex + 1) % cycle.length;
			applyAppColor(cycle[nextIndex]);
		};

		const init = (): void =>
		{
			getHtmlTag()?.setAttribute("data-theme", theme.value);
			initAnimations();
			initHoverOnFocus();
			initTts();
			initSyllables();

			initColor();
			initBlueLight();
			initFontFamily();
			initFontSize();
			initLetterSpacing();
			initCursor();
			initAppColor();

			if (window.speechSynthesis)
			{
				window.speechSynthesis.cancel();
			}

			// DEBUG: Apply settings from URL params (for debugging)
			debugApplyFromUrlParams();
		};

		/**
		 * DEBUG: Apply accessibility settings from URL query parameters
		 * Example: ?debug-a11y-font-size=200&debug-a11y-font-family=dyslexic&debug-a11y-letter-spacing=wider&debug-a11y-tts=on&debug-a11y-open-modal=1
		 * TODO: Remove this debug function when no longer needed
		 */
		const debugApplyFromUrlParams = (): void =>
		{
			if (typeof window === "undefined")
			{
				return;
			}

			const params = new URLSearchParams(window.location.search);

			// DEBUG: Check if any debug param is present
			const hasDebugParams = Array.from(params.keys()).some(key => key.startsWith("debug-a11y-"));
			if (! hasDebugParams)
			{
				return;
			}

			// DEBUG: Font size: ?debug-a11y-font-size=100|150|200
			const fontSizeParam = params.get("debug-a11y-font-size");
			if (fontSizeParam)
			{
				const key = fontSizeParam === "100"
					? "accessibility-font-size-default"
					: `accessibility-font-size-${ fontSizeParam }`;
				if (key in AccessibilityFontSizeEnum)
				{
					applyFontSize(key as keyof typeof AccessibilityFontSizeEnum);
				}
			}

			// DEBUG: Font family: ?debug-a11y-font-family=default|dyslexic|comic|atkinson|luciole|lexend
			const fontFamilyParam = params.get("debug-a11y-font-family");
			if (fontFamilyParam)
			{
				const familyMap: Record<string, keyof typeof AccessibilityFontFamilyEnum> = {
					"default": "accessibility-font-family-default",
					"dyslexic": "accessibility-font-family-open-dyslexic",
					"comic": "accessibility-font-family-comic-sans-ms",
					"atkinson": "accessibility-font-family-atkinson-hyperlegible",
					"luciole": "accessibility-font-family-luciole",
					"lexend": "accessibility-font-family-lexend",
				};
				if (fontFamilyParam in familyMap)
				{
					applyFontFamily(familyMap[fontFamilyParam]);
				}
			}

			// DEBUG: Letter spacing: ?debug-a11y-letter-spacing=normal|wide|wider|tight
			const letterSpacingParam = params.get("debug-a11y-letter-spacing");
			if (letterSpacingParam)
			{
				const spacingMap: Record<string, keyof typeof AccessibilityLetterSpacingEnum> = {
					"normal": "accessibility-letter-spacing-default",
					"tight": "accessibility-letter-spacing-tight",
					"wide": "accessibility-letter-spacing-wide",
					"wider": "accessibility-letter-spacing-wider",
				};
				if (letterSpacingParam in spacingMap)
				{
					applyLetterSpacing(spacingMap[letterSpacingParam]);
				}
			}

			// DEBUG: TTS: ?debug-a11y-tts=on|off
			const ttsParam = params.get("debug-a11y-tts");
			if (ttsParam === "on")
			{
				toggleTts(true);
			}

			// DEBUG: Theme: ?debug-a11y-theme=light|dark
			const themeParam = params.get("debug-a11y-theme");
			if (themeParam === "dark")
			{
				applyTheme("accessibility-theme-dark");
			}

			// DEBUG: Auto-open modal: ?debug-a11y-open-modal=1
			const openModalParam = params.get("debug-a11y-open-modal");
			if (openModalParam === "1")
			{
				// DEBUG: Poll for the element to exist (component might not be mounted yet)
				const waitForElementAndClick = (attempts = 0): void =>
				{
					const menuButton = document.getElementById("menu-accessibility");
					if (menuButton)
					{
						menuButton.click();
					}
					else if (attempts < 20)
					{
						// Retry every 100ms, up to 2 seconds
						setTimeout(() => waitForElementAndClick(attempts + 1), 100);
					}
				};
				waitForElementAndClick();
			}
		};

		const initForMobile = async (): Promise<void> =>
		{
			if (theme.value == "accessibility-theme-light")
			{
				getHtmlTag()?.classList.remove("ion-palette-dark");
				setBodyBackgroundColor("#E8E9EA");
			}
			else
			{
				getHtmlTag()?.classList.add("ion-palette-dark");
				setBodyBackgroundColor("#20212F");
			}

			initAnimations();
			initTts();
			initSyllables();

			initColor();
			initBlueLight();
			initFontFamily();
			initFontSize();
			initLetterSpacing();
			initAppColor();

			if (window.speechSynthesis)
			{
				window.speechSynthesis.cancel();
			}
		};

		const reset = (): void =>
		{
			animations.value = true;
			haptics.value = true;
			crashlytics.value = true;
			hoverOnFocus.value = false;
			tts.value = false;
			ttsRate.value = "accessibility-tts-rate-normal";
			ttsVoice.value = "accessibility-tts-voice-feminine";
			syllables.value = false;
			syllablesColors.value = "accessibility-syllables-blue-red";
			readingMask.value = false;
			readingMaskHeight.value = "accessibility-reading-mask-height-2";
			readingMaskOpacity.value = "accessibility-reading-mask-opacity-medium";
			theme.value = "accessibility-theme-light";
			color.value = "accessibility-color-default";
			blueLight.value = 0;
			fontFamily.value = "accessibility-font-family-default";
			fontSize.value = "accessibility-font-size-default";
			letterSpacing.value = "accessibility-letter-spacing-default";
			cursor.value = "accessibility-cursor-default";
			cursorSize.value = "accessibility-cursor-size-x1";
			cursorColor.value = "accessibility-cursor-color-default";
		};

		// theme + appColor = préférences de goût (thème clair/sombre, couleur de l'app),
		// pas des filtres d'accessibilité → volontairement exclus du badge de l'onglet.
		const isAccessibilityEnabled = computed(() =>
		{
			if (! animations.value)
			{
				return true;
			}

			if (hoverOnFocus.value)
			{
				return true;
			}

			if (tts.value)
			{
				return true;
			}

			if (syllables.value)
			{
				return true;
			}

			if (readingMask.value)
			{
				return true;
			}

			if (color.value != "accessibility-color-default")
			{
				return true;
			}

			if (blueLight.value > 0)
			{
				return true;
			}

			if (fontFamily.value != "accessibility-font-family-default")
			{
				return true;
			}

			if (fontSize.value != "accessibility-font-size-default")
			{
				return true;
			}

			if (letterSpacing.value != "accessibility-letter-spacing-default")
			{
				return true;
			}

			if (cursor.value != "accessibility-cursor-default" || cursorSize.value != "accessibility-cursor-size-x1" || cursorColor.value != "accessibility-cursor-color-default")
			{
				return true;
			}

			if (! haptics.value)
			{
				return true;
			}

			return false;
		});

		const isAnyModeEnabled = computed(() =>
			modeNight.value
			|| modeDyslexia.value
			|| modeDyslexia2.value
			|| modeMotorImpairments.value
			|| modeLowVision.value
			|| modeBlurredVision.value
			|| modeDeaf.value
			|| modeADHD.value
			|| modeMigraine.value
			|| modeSeniors.value
			|| modeEyeStrain.value
			|| modeLongReading.value
		);

		const getHtmlTag = (): HTMLElement | null =>
		{
			return document.querySelector("html") as HTMLElement;
		};

		const setBodyBackgroundColor = (hexColor: string): void =>
		{
			const body = document.querySelector("body") as HTMLBodyElement;

			if (body)
			{
				body.style.backgroundColor = hexColor;
			}
		};


		/**
		 * Animations
		 */
		const initAnimations = () =>
		{
			toggleAnimations(animations.value);
		};

		const toggleAnimations = (value: boolean) =>
		{
			const html = getHtmlTag();

			if (html)
			{
				if (! value)
				{
					html.classList.add("accessibility-animations-off");
				}
				else
				{
					html.classList.remove("accessibility-animations-off");
				}

				animations.value = value;
			}
		};

		/**
		 * Focus au survol
		 */
		const initHoverOnFocus = () =>
		{
			toggleHoverOnFocus(hoverOnFocus.value);
		};

		const toggleHoverOnFocus = (value: boolean) =>
		{
			const html = getHtmlTag();

			if (html)
			{
				if (value)
				{
					html.classList.add("accessibility-hover-on-focus");
				}
				else
				{
					html.classList.remove("accessibility-hover-on-focus");
				}

				hoverOnFocus.value = value;
			}
		};


		/**
		 * Lecteur vocal
		 */
		const initTts = (): void =>
		{
			toggleTts(tts.value);
		};

		const toggleTts = (value: boolean): void =>
		{
			const html = getHtmlTag();

			if (html)
			{
				if (value)
				{
					html.classList.add("accessibility-tts");
				}
				else
				{
					html.classList.remove("accessibility-tts");

					// Couper le lecteur ne suffit pas à faire taire la phrase commencée : la
					// directive rend son texte à l'élément, que la lecture avait remplacé par
					// son balisage de surlignage.
					stopAllSpeaking();
				}

				tts.value = value;
			}
		};

		const applyTtsRate = (value: keyof typeof AccessibilityTtsRateEnum): void =>
		{
			ttsRate.value = value;
		};

		const applyTtsVoice = (value: keyof typeof AccessibilityTtsVoiceEnum): void =>
		{
			ttsVoice.value = value;
		};

		const speak = (utterance: SpeechSynthesisUtterance): void =>
		{
			if (! (ttsObject instanceof SpeechSynthesis))
			{
				ttsObject = window.speechSynthesis;
			}

			if (ttsObject instanceof SpeechSynthesis)
			{
				ttsObject.cancel();
				const voices = ttsObject.getVoices();

				for (const voice of voices)
				{
					if (voice.lang == "fr-FR" && voice.localService)
					{
						utterance.voice = voice;
						break;
					}
				}

				utterance.rate = 1;
				utterance.pitch = 1;

				ttsObject.speak(utterance);
			}
		};

		const pause = (): void =>
		{
			if (ttsObject && ttsObject instanceof SpeechSynthesis)
			{
				ttsObject.pause();
			}
		};

		const shutUp = (): void =>
		{
			try
			{
				ttsObject.cancel();
			}
			catch (e)
			{ /* empty */
			}
		};

		/**
		 * Syllabification
		 */
		const initSyllables = (): void =>
		{
			if (syllables.value)
			{
				toggleSyllables(true);
			}
		};

		const toggleSyllables = (value: boolean): void =>
		{
			const html = getHtmlTag();

			if (html)
			{
				if (value)
				{
					html.classList.add("accessibility-syllables");
					// Apply current color scheme
					applySyllablesColors(syllablesColors.value);
				}
				else
				{
					html.classList.remove("accessibility-syllables");
					// Remove all color scheme classes
					html.classList.remove(
						"accessibility-syllables-blue-red",
						"accessibility-syllables-yellow-purple",
						"accessibility-syllables-green-orange",
						"accessibility-syllables-cyan-pink",
						"accessibility-syllables-teal-coral",
						"accessibility-syllables-lime-violet"
					);
				}

				syllables.value = value;
			}
		};

		const applySyllablesColors = (value: keyof typeof AccessibilitySyllablesColorsEnum): void =>
		{
			const html = getHtmlTag();

			if (html)
			{
				// Remove all color scheme classes
				html.classList.remove(
					"accessibility-syllables-blue-red",
					"accessibility-syllables-yellow-purple",
					"accessibility-syllables-green-orange",
					"accessibility-syllables-cyan-pink",
					"accessibility-syllables-teal-coral",
					"accessibility-syllables-lime-violet"
				);

				// Add new color scheme class
				html.classList.add(value);
				syllablesColors.value = value;
			}
		};

		/**
		 * Masque de lecture
		 */
		const toggleReadingMask = (value: boolean): void =>
		{
			readingMask.value = value;
		};

		const applyReadingMaskHeight = (value: keyof typeof AccessibilityReadingMaskHeightEnum): void =>
		{
			readingMaskHeight.value = value;
		};

		const applyReadingMaskOpacity = (value: keyof typeof AccessibilityReadingMaskOpacityEnum): void =>
		{
			readingMaskOpacity.value = value;
		};

		const toggleReadingMaskHeight = (): void =>
		{
			const heights = Object.keys(AccessibilityReadingMaskHeightEnum) as Array<keyof typeof AccessibilityReadingMaskHeightEnum>;
			const currentIndex = heights.indexOf(readingMaskHeight.value);
			const nextIndex = (currentIndex + 1) % heights.length;
			applyReadingMaskHeight(heights[nextIndex]);
		};

		const toggleReadingMaskOpacity = (): void =>
		{
			const opacities = Object.keys(AccessibilityReadingMaskOpacityEnum) as Array<keyof typeof AccessibilityReadingMaskOpacityEnum>;
			const currentIndex = opacities.indexOf(readingMaskOpacity.value);
			const nextIndex = (currentIndex + 1) % opacities.length;
			applyReadingMaskOpacity(opacities[nextIndex]);
		};

		/**
		 * Thème
		 */
		const toggleTheme = (value: boolean): void =>
		{
			if (value)
			{
				enableDarkTheme();
			}
			else
			{
				disableDarkTheme();
			}
		};

		const applyTheme = (value: keyof typeof AccessibilityThemeEnum): void =>
		{
			if (value == "accessibility-theme-light")
			{
				disableDarkTheme();
			}
			else
			{
				enableDarkTheme();
			}
		};

		const enableDarkTheme = (): void =>
		{
			theme.value = "accessibility-theme-dark";
			getHtmlTag()?.setAttribute("data-theme", theme.value);
		};

		const disableDarkTheme = (): void =>
		{
			theme.value = "accessibility-theme-light";
			getHtmlTag()?.setAttribute("data-theme", theme.value);
		};

		// Thème for mobile
		const toggleThemeForMobile = async (value: boolean): Promise<void> =>
		{
			if (value)
			{
				await enableDarkThemeForMobile();
			}
			else
			{
				await disableDarkThemeForMobile();
			}
		};

		const applyThemeForMobile = async (value: keyof typeof AccessibilityThemeEnum): Promise<void> =>
		{
			if (value == "accessibility-theme-light")
			{
				await disableDarkThemeForMobile();
			}
			else
			{
				await enableDarkThemeForMobile();
			}
		};

		const enableDarkThemeForMobile = async (): Promise<void> =>
		{
			theme.value = "accessibility-theme-dark";
			getHtmlTag()?.classList.add("ion-palette-dark");
			setBodyBackgroundColor("#20212F");
			await setNavigationBarColorDarkTheme();
		};

		const disableDarkThemeForMobile = async (): Promise<void> =>
		{
			theme.value = "accessibility-theme-light";
			getHtmlTag()?.classList.remove("ion-palette-dark");
			setBodyBackgroundColor("#E8E9EA");
			await setNavigationBarColorLightTheme();
		};

		const setStatusBarColor = (hex: string): void =>
		{
			// Supprimer tous les meta tags theme-color existants
			document.querySelectorAll("meta[name='theme-color']").forEach((meta) =>
			{
				meta.remove();
			});

			// Créer un nouveau meta tag avec la bonne couleur
			const meta = document.createElement("meta");
			meta.name = "theme-color";
			meta.content = hex;
			document.head.appendChild(meta);
		};


		const setStatusBarColorLightTheme = () =>
		{
			setStatusBarColor("#53796f");
		};

		const setStatusBarColorDarkTheme = () =>
		{
			setStatusBarColor("#385050");
		};

		const setStatusBarColorLightThemeForIndex = () =>
		{
			setStatusBarColor("#53C0FF");
		};

		const setStatusBarColorDarkThemeForIndex = () =>
		{
			setStatusBarColor("#312C85");
		};

		// Navigation bar functions - these are no-ops in web/admin
		// For ionic, import from @/utils/navigation-bar.utils.ts instead
		const setNavigationBarColor = async (_hex: string, _darkButtons: boolean = false): Promise<void> =>
		{
			// No-op for web/admin - use ionic/src/utils/navigation-bar.utils.ts for mobile
		};

		const setNavigationBarColorLightTheme = async (): Promise<void> =>
		{
			// No-op for web/admin
		};

		const setNavigationBarColorDarkTheme = async (): Promise<void> =>
		{
			// No-op for web/admin
		};

		const setNavigationBarColorTransparent = async (): Promise<void> =>
		{
			// No-op for web/admin
		};

		const setBodyBackgroundColorLightTheme = () =>
		{
			setBodyBackgroundColor("#E8E9EA");
		};

		const setBodyBackgroundColorDarkTheme = () =>
		{
			setBodyBackgroundColor("#20212F");
		};

		const setBodyBackgroundColorLightThemeForIndex = () =>
		{
			setBodyBackgroundColor("#53C0FF");
		};

		const setBodyBackgroundColorDarkThemeForIndex = () =>
		{
			setBodyBackgroundColor("#312C85");
		};

		/**
		 * Colorimétrie
		 */
		const initColor = (): void =>
		{
			applyColor(color.value);
		};

		const applyColor = (value: keyof typeof AccessibilityColorEnum) =>
		{
			const html = getHtmlTag();

			if (html)
			{
				html.classList.remove(
					"accessibility-color-default",
					"accessibility-color-high-contrast",
					"accessibility-color-black-and-white",
					"accessibility-color-protanopia",
					"accessibility-color-deuteranopia",
					"accessibility-color-tritanopia"
				);

				if (html.classList.contains(value))
				{
					html.classList.remove(value);
				}
				else
				{
					html.classList.add(value);
				}

				color.value = value;
			}
		};

		const toggleColor = () =>
		{
			if (color.value == "accessibility-color-default")
			{
				applyColor("accessibility-color-high-contrast");
			}
			else if (color.value == "accessibility-color-high-contrast")
			{
				applyColor("accessibility-color-black-and-white");
			}
			else if (color.value == "accessibility-color-black-and-white")
			{
				applyColor("accessibility-color-protanopia");
			}
			else if (color.value == "accessibility-color-protanopia")
			{
				applyColor("accessibility-color-deuteranopia");
			}
			else if (color.value == "accessibility-color-deuteranopia")
			{
				applyColor("accessibility-color-tritanopia");
			}
			else if (color.value == "accessibility-color-tritanopia")
			{
				applyColor("accessibility-color-default");
			}
		};

		const toggleColorReverse = () =>
		{
			if (color.value == "accessibility-color-default")
			{
				applyColor("accessibility-color-tritanopia");
			}
			else if (color.value == "accessibility-color-tritanopia")
			{
				applyColor("accessibility-color-deuteranopia");
			}
			else if (color.value == "accessibility-color-deuteranopia")
			{
				applyColor("accessibility-color-protanopia");
			}
			else if (color.value == "accessibility-color-protanopia")
			{
				applyColor("accessibility-color-black-and-white");
			}
			else if (color.value == "accessibility-color-black-and-white")
			{
				applyColor("accessibility-color-high-contrast");
			}
			else if (color.value == "accessibility-color-high-contrast")
			{
				applyColor("accessibility-color-default");
			}
		};

		/**
		 * Lumière bleue
		 */
		const initBlueLight = (): void =>
		{
			applyBlueLight(blueLight.value);
		};

		const applyBlueLight = (value: number): void =>
		{
			// Rechercher ou créer une balise <style> pour gérer les pseudo-éléments
			let styleElement = document.getElementById("filter-style");
			if (! styleElement)
			{
				styleElement = document.createElement("style");
				styleElement.id = "filter-style";
				document.head.appendChild(styleElement);
			}

			// Mettre à jour le style du pseudo-élément
			const sepia = value / 100;
			const brightness = 1 - value / 300;
			styleElement.textContent = `
				body::before {
					content: '';
					position: fixed;
					top: 0;
					left: 0;
					width: 100%;
					height: 100%;
					z-index: 9999;
					pointer-events: none;
					backdrop-filter: sepia(${ sepia }) brightness(${ brightness });
				}
			`;
			blueLight.value = value;
		};

		/**
		 * Font Family
		 */
		const initFontFamily = (): void =>
		{
			applyFontFamily(fontFamily.value);
		};

		const applyFontFamily = (value: keyof typeof AccessibilityFontFamilyEnum): void =>
		{
			const html = getHtmlTag();

			if (html)
			{
				html.classList.remove(
					"accessibility-font-family-comic-sans-ms",
					"accessibility-font-family-open-dyslexic",
					"accessibility-font-family-atkinson-hyperlegible",
					"accessibility-font-family-luciole",
					"accessibility-font-family-lexend");

				if (value !== "accessibility-font-family-default")
				{
					if (html.classList.contains(value))
					{
						html.classList.remove(value);
					}
					else
					{
						html.classList.add(value);
					}
				}

				fontFamily.value = value;
			}
		};

		const toggleFontFamily = () =>
		{
			if (fontFamily.value == "accessibility-font-family-default")
			{
				applyFontFamily("accessibility-font-family-comic-sans-ms");
			}
			else if (fontFamily.value == "accessibility-font-family-comic-sans-ms")
			{
				applyFontFamily("accessibility-font-family-open-dyslexic");
			}
			else if (fontFamily.value == "accessibility-font-family-open-dyslexic")
			{
				applyFontFamily("accessibility-font-family-atkinson-hyperlegible");
			}
			else if (fontFamily.value == "accessibility-font-family-atkinson-hyperlegible")
			{
				applyFontFamily("accessibility-font-family-luciole");
			}
			else if (fontFamily.value == "accessibility-font-family-luciole")
			{
				applyFontFamily("accessibility-font-family-lexend");
			}
			else if (fontFamily.value == "accessibility-font-family-lexend")
			{
				applyFontFamily("accessibility-font-family-default");
			}
		};

		const toggleFontFamilyReverse = () =>
		{
			if (fontFamily.value == "accessibility-font-family-default")
			{
				applyFontFamily("accessibility-font-family-lexend");
			}
			else if (fontFamily.value == "accessibility-font-family-lexend")
			{
				applyFontFamily("accessibility-font-family-luciole");
			}
			else if (fontFamily.value == "accessibility-font-family-luciole")
			{
				applyFontFamily("accessibility-font-family-atkinson-hyperlegible");
			}
			else if (fontFamily.value == "accessibility-font-family-atkinson-hyperlegible")
			{
				applyFontFamily("accessibility-font-family-open-dyslexic");
			}
			else if (fontFamily.value == "accessibility-font-family-open-dyslexic")
			{
				applyFontFamily("accessibility-font-family-comic-sans-ms");
			}
			else if (fontFamily.value == "accessibility-font-family-comic-sans-ms")
			{
				applyFontFamily("accessibility-font-family-default");
			}
		};

		/**
		 * Font Size
		 */
		const initFontSize = (): void =>
		{
			applyFontSize(fontSize.value);
		};

		const applyFontSize = (value: keyof typeof AccessibilityFontSizeEnum): void =>
		{
			const html = getHtmlTag();

			if (html)
			{
				html.classList.remove(
					"accessibility-font-size-default",
					"accessibility-font-size-150",
					"accessibility-font-size-200"
				);

				if (value == "accessibility-font-size-150")
				{
					html.classList.add("accessibility-font-size-150");
				}
				else if (value == "accessibility-font-size-200")
				{
					html.classList.add("accessibility-font-size-150", "accessibility-font-size-200");
				}

				fontSize.value = value;
			}
		};

		const toggleFontSize = (): void =>
		{
			if (fontSize.value == "accessibility-font-size-default")
			{
				applyFontSize("accessibility-font-size-150");
			}
			else if (fontSize.value == "accessibility-font-size-150")
			{
				applyFontSize("accessibility-font-size-200");
			}
			else if (fontSize.value == "accessibility-font-size-200")
			{
				applyFontSize("accessibility-font-size-default");
			}
		};

		const toggleFontSizeReverse = (): void =>
		{
			if (fontSize.value == "accessibility-font-size-default")
			{
				applyFontSize("accessibility-font-size-200");
			}
			else if (fontSize.value == "accessibility-font-size-200")
			{
				applyFontSize("accessibility-font-size-150");
			}
			else if (fontSize.value == "accessibility-font-size-150")
			{
				applyFontSize("accessibility-font-size-default");
			}
		};

		/**
		 * Letter Spacing
		 */
		const initLetterSpacing = (): void =>
		{
			applyLetterSpacing(letterSpacing.value);
		};

		const applyLetterSpacing = (value: keyof typeof AccessibilityLetterSpacingEnum): void =>
		{
			const html = getHtmlTag();

			if (html)
			{
				html.classList.remove(
					"accessibility-letter-spacing-default",
					"accessibility-letter-spacing-tight",
					"accessibility-letter-spacing-wide",
					"accessibility-letter-spacing-wider"
				);

				if (value == "accessibility-letter-spacing-tight")
				{
					html.classList.add("accessibility-letter-spacing-tight");
				}
				else if (value == "accessibility-letter-spacing-wide")
				{
					html.classList.add("accessibility-letter-spacing-wide");
				}
				else if (value == "accessibility-letter-spacing-wider")
				{
					html.classList.add("accessibility-letter-spacing-wide", "accessibility-letter-spacing-wider");
				}

				letterSpacing.value = value;
			}
		};

		const toggleLetterSpacing = (): void =>
		{
			if (letterSpacing.value == "accessibility-letter-spacing-tight")
			{
				applyLetterSpacing("accessibility-letter-spacing-default");
			}
			else if (letterSpacing.value == "accessibility-letter-spacing-default")
			{
				applyLetterSpacing("accessibility-letter-spacing-wide");
			}
			else if (letterSpacing.value == "accessibility-letter-spacing-wide")
			{
				applyLetterSpacing("accessibility-letter-spacing-wider");
			}
			else if (letterSpacing.value == "accessibility-letter-spacing-wider")
			{
				applyLetterSpacing("accessibility-letter-spacing-tight");
			}
		};

		const toggleLetterSpacingReverse = (): void =>
		{
			if (letterSpacing.value == "accessibility-letter-spacing-tight")
			{
				applyLetterSpacing("accessibility-letter-spacing-wider");
			}
			else if (letterSpacing.value == "accessibility-letter-spacing-wider")
			{
				applyLetterSpacing("accessibility-letter-spacing-wide");
			}
			else if (letterSpacing.value == "accessibility-letter-spacing-wide")
			{
				applyLetterSpacing("accessibility-letter-spacing-default");
			}
			else if (letterSpacing.value == "accessibility-letter-spacing-default")
			{
				applyLetterSpacing("accessibility-letter-spacing-tight");
			}
		};

		/**
		 * Curseur de la souris
		 */
		const initCursor = (): void =>
		{
			applyCursor(cursor.value, cursorSize.value, cursorColor.value);
		};

		const applyCursor = (value: keyof typeof AccessibilityCursorEnum | null, size: keyof typeof AccessibilityCursorSizeEnum | null, color: keyof typeof AccessibilityCursorColorEnum | null): void =>
		{
			if (value && size && color)
			{
				const html = getHtmlTag();

				if (html)
				{
					html.classList.remove(
						"accessibility-cursor-default-x1-default",
						"accessibility-cursor-default-x1-white",
						"accessibility-cursor-default-x1-red",
						"accessibility-cursor-default-x1-blue",
						"accessibility-cursor-default-x1-green",
						"accessibility-cursor-default-x1-yellow",
						"accessibility-cursor-default-x1-pink",
						"accessibility-cursor-default-x2-default",
						"accessibility-cursor-default-x2-white",
						"accessibility-cursor-default-x2-red",
						"accessibility-cursor-default-x2-blue",
						"accessibility-cursor-default-x2-green",
						"accessibility-cursor-default-x2-yellow",
						"accessibility-cursor-default-x2-pink",
						"accessibility-cursor-default-x3-default",
						"accessibility-cursor-default-x3-white",
						"accessibility-cursor-default-x3-red",
						"accessibility-cursor-default-x3-blue",
						"accessibility-cursor-default-x3-green",
						"accessibility-cursor-default-x3-yellow",
						"accessibility-cursor-default-x3-pink",

						"accessibility-cursor-hand-x1-default",
						"accessibility-cursor-hand-x1-white",
						"accessibility-cursor-hand-x1-red",
						"accessibility-cursor-hand-x1-blue",
						"accessibility-cursor-hand-x1-green",
						"accessibility-cursor-hand-x1-yellow",
						"accessibility-cursor-hand-x1-pink",
						"accessibility-cursor-hand-x2-default",
						"accessibility-cursor-hand-x2-white",
						"accessibility-cursor-hand-x2-red",
						"accessibility-cursor-hand-x2-blue",
						"accessibility-cursor-hand-x2-green",
						"accessibility-cursor-hand-x2-yellow",
						"accessibility-cursor-hand-x2-pink",
						"accessibility-cursor-hand-x3-default",
						"accessibility-cursor-hand-x3-white",
						"accessibility-cursor-hand-x3-red",
						"accessibility-cursor-hand-x3-blue",
						"accessibility-cursor-hand-x3-green",
						"accessibility-cursor-hand-x3-yellow",
						"accessibility-cursor-hand-x3-pink",

						"accessibility-cursor-pointer-x1-default",
						"accessibility-cursor-pointer-x1-white",
						"accessibility-cursor-pointer-x1-red",
						"accessibility-cursor-pointer-x1-blue",
						"accessibility-cursor-pointer-x1-green",
						"accessibility-cursor-pointer-x1-yellow",
						"accessibility-cursor-pointer-x1-pink",
						"accessibility-cursor-pointer-x2-default",
						"accessibility-cursor-pointer-x2-white",
						"accessibility-cursor-pointer-x2-red",
						"accessibility-cursor-pointer-x2-blue",
						"accessibility-cursor-pointer-x2-green",
						"accessibility-cursor-pointer-x2-yellow",
						"accessibility-cursor-pointer-x2-pink",
						"accessibility-cursor-pointer-x3-default",
						"accessibility-cursor-pointer-x3-white",
						"accessibility-cursor-pointer-x3-red",
						"accessibility-cursor-pointer-x3-blue",
						"accessibility-cursor-pointer-x3-green",
						"accessibility-cursor-pointer-x3-yellow",
						"accessibility-cursor-pointer-x3-pink",

						"accessibility-cursor-cross-x1-default",
						"accessibility-cursor-cross-x1-white",
						"accessibility-cursor-cross-x1-red",
						"accessibility-cursor-cross-x1-blue",
						"accessibility-cursor-cross-x1-green",
						"accessibility-cursor-cross-x1-yellow",
						"accessibility-cursor-cross-x1-pink",
						"accessibility-cursor-cross-x2-default",
						"accessibility-cursor-cross-x2-white",
						"accessibility-cursor-cross-x2-red",
						"accessibility-cursor-cross-x2-blue",
						"accessibility-cursor-cross-x2-green",
						"accessibility-cursor-cross-x2-yellow",
						"accessibility-cursor-cross-x2-pink",
						"accessibility-cursor-cross-x3-default",
						"accessibility-cursor-cross-x3-white",
						"accessibility-cursor-cross-x3-red",
						"accessibility-cursor-cross-x3-blue",
						"accessibility-cursor-cross-x3-green",
						"accessibility-cursor-cross-x3-yellow",
						"accessibility-cursor-cross-x3-pink",

						"accessibility-cursor-target-x1-default",
						"accessibility-cursor-target-x1-white",
						"accessibility-cursor-target-x1-red",
						"accessibility-cursor-target-x1-blue",
						"accessibility-cursor-target-x1-green",
						"accessibility-cursor-target-x1-yellow",
						"accessibility-cursor-target-x1-pink",
						"accessibility-cursor-target-x2-default",
						"accessibility-cursor-target-x2-white",
						"accessibility-cursor-target-x2-red",
						"accessibility-cursor-target-x2-blue",
						"accessibility-cursor-target-x2-green",
						"accessibility-cursor-target-x2-yellow",
						"accessibility-cursor-target-x2-pink",
						"accessibility-cursor-target-x3-default",
						"accessibility-cursor-target-x3-white",
						"accessibility-cursor-target-x3-red",
						"accessibility-cursor-target-x3-blue",
						"accessibility-cursor-target-x3-green",
						"accessibility-cursor-target-x3-yellow",
						"accessibility-cursor-target-x3-pink",
					);

					let classCss = value + "-";

					switch (size)
					{
						case "accessibility-cursor-size-x1":
							classCss += "x1-";
							break;

						case "accessibility-cursor-size-x2":
							classCss += "x2-";
							break;

						case "accessibility-cursor-size-x3":
							classCss += "x3-";
							break;
					}

					switch (color)
					{
						case "accessibility-cursor-color-default":
							classCss += "default";
							break;
						case "accessibility-cursor-color-red":
							classCss += "red";
							break;
						case "accessibility-cursor-color-blue":
							classCss += "blue";
							break;
						case "accessibility-cursor-color-green":
							classCss += "green";
							break;
						case "accessibility-cursor-color-yellow":
							classCss += "yellow";
							break;
						case "accessibility-cursor-color-pink":
							classCss += "pink";
							break;
					}

					html.classList.add(classCss);
					cursor.value = value;
					cursorSize.value = size;
					cursorColor.value = color;
				}
			}
		};

		/**
		 * Modes
		 */
		const resetAllModes = () =>
		{
			modeNight.value = false;
			modeDyslexia.value = false;
			modeDyslexia2.value = false;
			modeMotorImpairments.value = false;
			modeLowVision.value = false;
			modeBlurredVision.value = false;
			modeDeaf.value = false;
			modeADHD.value = false;
			modeMigraine.value = false;
			modeSeniors.value = false;
			modeEyeStrain.value = false;
			modeLongReading.value = false;
		};

		const initModes = () =>
		{
			toggleAnimations(true);
			toggleHoverOnFocus(false);
			disableDarkTheme();
			applyColor("accessibility-color-default");
			applyBlueLight(0);
			applyFontFamily("accessibility-font-family-default");
			applyFontSize("accessibility-font-size-default");
			applyLetterSpacing("accessibility-letter-spacing-default");
			applyCursor("accessibility-cursor-default", "accessibility-cursor-size-x1", "accessibility-cursor-color-default");
			toggleTts(false);
			toggleSyllables(false);
			toggleReadingMask(false);
		};

		const updateModeNight = () =>
		{
			const newValue = ! modeNight.value;
			resetAllModes();
			modeNight.value = newValue;
			initModes();

			if (modeNight.value)
			{
				enableDarkTheme();
				applyBlueLight(30);
				applyFontFamily("accessibility-font-family-atkinson-hyperlegible");
			}
		};

		const updateModeDyslexia = () =>
		{
			const newValue = ! modeDyslexia.value;
			resetAllModes();
			modeDyslexia.value = newValue;
			initModes();

			if (modeDyslexia.value)
			{
				applyBlueLight(10);
				applyFontFamily("accessibility-font-family-open-dyslexic");
				applyLetterSpacing("accessibility-letter-spacing-wide");
			}
		};

		const updateModeDyslexia2 = () =>
		{
			const newValue = ! modeDyslexia2.value;
			resetAllModes();
			modeDyslexia2.value = newValue;
			initModes();

			if (modeDyslexia2.value)
			{
				applyBlueLight(10);
				applyFontFamily("accessibility-font-family-open-dyslexic");
				applyLetterSpacing("accessibility-letter-spacing-wide");
				toggleSyllables(true);
			}
		};

		const updateModeMotorImpairments = () =>
		{
			const newValue = ! modeMotorImpairments.value;
			resetAllModes();
			modeMotorImpairments.value = newValue;
			initModes();

			if (modeMotorImpairments.value)
			{
				toggleHoverOnFocus(true);
				applyFontSize("accessibility-font-size-150");
				applyCursor("accessibility-cursor-pointer", "accessibility-cursor-size-x2", "accessibility-cursor-color-default");
			}
		};

		const updateModeLowVision = () =>
		{
			const newValue = ! modeLowVision.value;
			resetAllModes();
			modeLowVision.value = newValue;
			initModes();

			if (modeLowVision.value)
			{
				toggleHoverOnFocus(true);
				applyFontSize("accessibility-font-size-150");
				applyCursor(
					"accessibility-cursor-cross",
					"accessibility-cursor-size-x3",
					"accessibility-cursor-color-red"
				);
			}
		};

		const updateModeBlurredVision = () =>
		{
			const newValue = ! modeBlurredVision.value;
			resetAllModes();
			modeBlurredVision.value = newValue;
			initModes();

			if (modeBlurredVision.value)
			{
				toggleHoverOnFocus(true);
				applyColor("accessibility-color-high-contrast");
				applyFontFamily("accessibility-font-family-luciole");
				applyFontSize("accessibility-font-size-200");
				applyLetterSpacing("accessibility-letter-spacing-wider");
				applyCursor(
					"accessibility-cursor-cross",
					"accessibility-cursor-size-x3",
					"accessibility-cursor-color-red"
				);
			}
		};

		const updateModeDeaf = () =>
		{
			const newValue = ! modeDeaf.value;
			resetAllModes();
			modeDeaf.value = newValue;
			initModes();

			if (modeDeaf.value)
			{
			}
		};

		const updateModeADHD = () =>
		{
			const newValue = ! modeADHD.value;
			resetAllModes();
			modeADHD.value = newValue;
			initModes();

			if (modeADHD.value)
			{
				toggleAnimations(false);
				toggleReadingMask(true);
				toggleTts(true);
			}
		};

		const updateModeMigraine = () =>
		{
			const newValue = ! modeMigraine.value;
			resetAllModes();
			modeMigraine.value = newValue;
			initModes();

			if (modeMigraine.value)
			{
				enableDarkTheme();
				applyBlueLight(50);
				toggleAnimations(false);
			}
		};

		const updateModeSeniors = () =>
		{
			const newValue = ! modeSeniors.value;
			resetAllModes();
			modeSeniors.value = newValue;
			initModes();

			if (modeSeniors.value)
			{
				applyFontSize("accessibility-font-size-150");
				applyLetterSpacing("accessibility-letter-spacing-wide");
				applyCursor("accessibility-cursor-pointer", "accessibility-cursor-size-x2", "accessibility-cursor-color-default");
				toggleTts(true);
			}
		};

		const updateModeEyeStrain = () =>
		{
			const newValue = ! modeEyeStrain.value;
			resetAllModes();
			modeEyeStrain.value = newValue;
			initModes();

			if (modeEyeStrain.value)
			{
				enableDarkTheme();
				applyBlueLight(25);
				applyFontSize("accessibility-font-size-150");
			}
		};

		const updateModeLongReading = () =>
		{
			const newValue = ! modeLongReading.value;
			resetAllModes();
			modeLongReading.value = newValue;
			initModes();

			if (modeLongReading.value)
			{
				applyBlueLight(15);
				applyFontFamily("accessibility-font-family-lexend");
				applyLetterSpacing("accessibility-letter-spacing-wide");
				toggleReadingMask(true);
			}
		};

		return {
			isScrolling,

			isDarkTheme,
			isFs200,
			needsCompactLayout,
			needsExtraCompactLayout,

			init,
			initForMobile,
			reset,
			getHtmlTag,
			isAccessibilityEnabled,
			isAnyModeEnabled,

			// App color
			appColor,
			applyAppColor,
			toggleAppColor,

			// Animations
			animations,
			toggleAnimations,

			// Haptics
			haptics,
			crashlytics,

			// Hover on focus
			hoverOnFocus,
			toggleHoverOnFocus,

			// TTS
			tts,
			ttsRate,
			ttsVoice,
			toggleTts,
			applyTtsRate,
			applyTtsVoice,
			speak,
			pause,
			shutUp,

			// LSF

			// Syllables
			syllables,
			syllablesColors,
			toggleSyllables,
			applySyllablesColors,

			// Reading Mask
			readingMask,
			readingMaskHeight,
			readingMaskOpacity,
			toggleReadingMask,
			applyReadingMaskHeight,
			applyReadingMaskOpacity,
			toggleReadingMaskHeight,
			toggleReadingMaskOpacity,

			// Thème
			theme,
			getTheme,
			toggleTheme,
			applyTheme,
			enableDarkTheme,
			disableDarkTheme,
			toggleThemeForMobile,
			applyThemeForMobile,
			enableDarkThemeForMobile,
			disableDarkThemeForMobile,

			setStatusBarColor,
			setStatusBarColorLightTheme,
			setStatusBarColorDarkTheme,
			setStatusBarColorLightThemeForIndex,
			setStatusBarColorDarkThemeForIndex,
			setNavigationBarColor,
			setNavigationBarColorLightTheme,
			setNavigationBarColorDarkTheme,
			setNavigationBarColorTransparent,
			setBodyBackgroundColor,
			setBodyBackgroundColorLightTheme,
			setBodyBackgroundColorDarkTheme,
			setBodyBackgroundColorLightThemeForIndex,
			setBodyBackgroundColorDarkThemeForIndex,

			// Colorimétrie
			color,
			applyColor,
			toggleColor,
			toggleColorReverse,

			// Lumière bleue
			blueLight,
			initBlueLight,
			applyBlueLight,

			// Font Family
			fontFamily,
			applyFontFamily,
			toggleFontFamily,
			toggleFontFamilyReverse,

			// Font Size
			fontSize,
			applyFontSize,
			toggleFontSize,
			toggleFontSizeReverse,

			// Letter Spacing
			letterSpacing,
			applyLetterSpacing,
			toggleLetterSpacing,
			toggleLetterSpacingReverse,

			// Curseur de la souris
			cursor,
			cursorSize,
			cursorColor,
			applyCursor,

			// Modes
			modeNight,
			modeDyslexia,
			modeDyslexia2,
			modeMotorImpairments,
			modeLowVision,
			modeBlurredVision,
			modeDeaf,
			modeADHD,
			modeMigraine,
			modeSeniors,
			modeEyeStrain,
			modeLongReading,
			updateModeNight,
			updateModeDyslexia,
			updateModeDyslexia2,
			updateModeMotorImpairments,
			updateModeLowVision,
			updateModeBlurredVision,
			updateModeDeaf,
			updateModeADHD,
			updateModeMigraine,
			updateModeSeniors,
			updateModeEyeStrain,
			updateModeLongReading,
		};
	},
	{
		// `{ enabled: true }` et non `true` : `pinia-plugin-capacitor-persist` (ionic, coach) ignore tout
		// le reste, et `pinia-plugin-persistedstate` (web, admin) prend l'objet comme options par défaut.
		// @ts-ignore
		persist: { enabled: true },
	},
);

export default useAccessibilityStore;
